/**
 * SDE Phase 2 (s88) — Secure File save service.
 *
 * Reusable build-and-persist for the encrypted .oesif Secure Information File. Reads the
 * in-memory Cat A values (from SensitiveDataSessionStore.getAllValues()), serializes them
 * into the container body, encrypts via the existing secure-file lib, and persists:
 *   - existing handle (Chrome/Edge) → silent write
 *   - first save on Chrome/Edge      → native save picker (returns a handle for next time)
 *   - Safari/Firefox                 → browser download
 *
 * Import ONLY from "@/lib/secure-file" (the encryption lib's public index) — the module
 * boundary forbids reaching into its internals.
 */
import {
  buildContainer,
  saveToHandle,
  triggerDownload,
  supportsFileSystemAccess,
  FILE_EXTENSION,
} from "@/lib/secure-file";
import { PRODUCT_NAME } from "@shared/brand";

/**
 * 1e — the default "no passphrase" key. This is NOT real security: anyone with the file
 * AND this source can derive it. It only stops the .oesif from being readable by
 * double-clicking it. Real protection is the OPTIONAL user passphrase.
 */
export const NO_PASSPHRASE_KEY = "oe-secure-file-default-v1";

// Fallback filename when the caller doesn't supply a descriptive one (s111). FILE_EXTENSION
// already includes the leading dot, so it is NOT prefixed with another "." here.
export const SECURE_FILE_NAME = `organized-estate-secure-info${FILE_EXTENSION}`;

/** Build the container body from the in-memory store values. `data` (the composite-key
 *  map) round-trips cleanly into loadValues() on Phase 3 open; `plaintext` is a
 *  human-readable fallback. */
export function serializeSecureFile(values: Record<string, string>): { data: unknown; plaintext: string } {
  const savedAt = new Date().toISOString();
  const data = { version: 1, savedAt, values };
  const plaintext = [
    `${PRODUCT_NAME} — Secure Information File`,
    `Saved: ${savedAt}`,
    "",
    ...Object.entries(values).map(([k, v]) => `${k}: ${v}`),
    "",
    `(Open this file in ${PRODUCT_NAME} to view these values in context.)`,
  ].join("\n");
  return { data, plaintext };
}

export interface SaveSecureFileInput {
  values: Record<string, string>;
  /** Empty/undefined → the NO_PASSPHRASE_KEY default. */
  passphrase?: string | null;
  /** An existing handle for a silent write (Chrome/Edge). */
  handle?: FileSystemFileHandle | null;
  /** s111: descriptive filename set at creation (picker suggestedName + Safari/Firefox
   *  download). Undefined → SECURE_FILE_NAME. Ignored on the handle path (name fixed at
   *  creation — the handle already points at the existing file). */
  fileName?: string;
}

export interface SaveSecureFileResult {
  /** Possibly newly-acquired handle (Chrome/Edge picker). null on Safari/Firefox. */
  handle: FileSystemFileHandle | null;
  method: "handle" | "picker" | "download";
}

/**
 * Build + persist the secure file. Callers should catch errors; a user-cancelled picker
 * throws `AbortError` (treat as a no-op, not a failure).
 */
export async function saveSecureFile(input: SaveSecureFileInput): Promise<SaveSecureFileResult> {
  const { data, plaintext } = serializeSecureFile(input.values);
  const passphrase = input.passphrase && input.passphrase.length > 0 ? input.passphrase : NO_PASSPHRASE_KEY;
  const bytes = await buildContainer({ data, plaintext, passphrase });

  if (input.handle) {
    await saveToHandle(input.handle, bytes);
    return { handle: input.handle, method: "handle" };
  }

  const fileName = input.fileName || SECURE_FILE_NAME;

  if (supportsFileSystemAccess()) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handle = (await (window as any).showSaveFilePicker({
      suggestedName: fileName,
      types: [{
        description: `${PRODUCT_NAME} Secure Information File`,
        accept: { "application/octet-stream": [FILE_EXTENSION] },
      }],
    })) as FileSystemFileHandle;
    await saveToHandle(handle, bytes);
    return { handle, method: "picker" };
  }

  triggerDownload(bytes, fileName);
  return { handle: null, method: "download" };
}
